import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import QuickActions from '../components/dashboard/QuickActions';
import ActiveJobsList from '../components/dashboard/ActiveJobsList';
import './OwnerDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const OwnerDashboard = () => {
  const { user } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ vessels: 0, activeJobs: 0, activeContracts: 0, certificates: 0 });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('marilink_token');
      const [vesselsRes, rfqsRes, contractsRes, certsRes] = await Promise.all([
        fetch(`${API_BASE}/vessels`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/rfqs`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/contracts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/certificates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const vesselsData = await vesselsRes.json();
      const rfqsData = await rfqsRes.json();
      const contractsData = await contractsRes.json();
      const certsData = await certsRes.json();

      const activeJobsCount = rfqsData.rfqs?.filter(r => ['open', 'bidding', 'in_progress'].includes(r.status)).length || 0;

      let activeContractCount = 0;
      let needApproval = [];
      if (contractsData.success) {
        const contracts = contractsData.contracts || [];
        activeContractCount = contracts.filter(c => ['payment_verified', 'work_in_progress'].includes(c.status)).length;
        needApproval = contracts.filter(c => c.status === 'work_completed');
      }

      const expiringCerts = certsData.certificates?.filter(c => c.status === 'warning' || c.status === 'critical').length || 0;

      setStats({
        vessels: vesselsData.vessels?.length || 0,
        activeJobs: activeJobsCount,
        activeContracts: activeContractCount,
        certificates: expiringCerts
      });
      setPendingApprovals(needApproval);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchData();
    const events = [
      'vessel_added', 'vessel_updated', 'vessel_deleted',
      'new_bid', 'contract_created', 'work_completed',
      'contract_status_change', 'bid_accepted', 'new_rfq',
      'payment_released'
    ];
    events.forEach(event => socket.on(event, refresh));
    return () => events.forEach(event => socket.off(event, refresh));
  }, [socket, fetchData]);

  const handleApproveCompletion = async (contractId) => {
    if (!window.confirm('Confirm this work as complete? This will finalize the contract.')) return;
    try {
      const token = localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/contracts/${contractId}/complete-work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.message || 'Failed to confirm completion');
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-spinner fa-spin"></i>
    </div>
  );

  const safeVessels = Number(stats.vessels) || 0;
  const safeActiveJobs = Number(stats.activeJobs) || 0;
  const safeActiveContracts = Number(stats.activeContracts) || 0;
  const safeCertificates = Number(stats.certificates) || 0;
  const firstName = user?.name?.split(' ')[0] || 'User';
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
  });

  return (
    <div className="owner-dashboard">
      <div className="welcome-banner">
        <div className="welcome-text">
          <h1>Overview, {firstName}</h1>
          <p>System status and operational metrics</p>
        </div>
        <div className="welcome-date">
          <i className="fa-regular fa-calendar"></i> {currentDate}
        </div>
      </div>

      <div className="od-stats-row">
        <div className="od-stat-box" onClick={() => navigate('/owner/my-ships')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{safeVessels}</span>
            <span className="od-stat-lbl">Fleet Size</span>
          </div>
          <i className="fa-solid fa-ship od-stat-icon"></i>
        </div>

        <div className="od-stat-box" onClick={() => navigate('/owner/my-jobs-quotations')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{safeActiveJobs}</span>
            <span className="od-stat-lbl">Active Jobs</span>
          </div>
          <i className="fa-solid fa-briefcase od-stat-icon"></i>
        </div>

        <div className="od-stat-box" onClick={() => navigate('/owner/my-jobs-quotations')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{safeActiveContracts}</span>
            <span className="od-stat-lbl">Active Contracts</span>
          </div>
          <i className="fa-solid fa-file-contract od-stat-icon"></i>
        </div>

        <div className="od-stat-box" onClick={() => navigate('/owner/certificates')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{safeCertificates}</span>
            <span className="od-stat-lbl">Certificate Alerts</span>
          </div>
          <i
            className="fa-solid fa-file-shield od-stat-icon"
            style={{ color: safeCertificates > 0 ? '#ef4444' : '#18cb96' }}
          ></i>
        </div>
      </div>

      {pendingApprovals.length > 0 && (
        <div className="od-alerts-wrapper">
          {pendingApprovals.map(contract => (
            <div key={contract._id} className="od-alert-item approval">
              <div className="od-alert-left">
                <i className="fa-solid fa-clipboard-check od-alert-icon approval"></i>
                <div className="od-alert-text">
                  <h4>Confirm Completion</h4>
                  <p>{contract.vesselName} - {contract.serviceType} by {contract.vendorName}</p>
                </div>
              </div>
              <button onClick={() => handleApproveCompletion(contract._id)} className="od-btn-action">
                Confirm Complete <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="od-grid">
        <div className="od-col-main">
          <ActiveJobsList userRole="owner" />
        </div>
        <div className="od-col-side">
          <QuickActions userRole="owner" />
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;